import React, { useRef, useState, useEffect, useCallback, useMemo, forwardRef } from "react";
import FilterDropdown from "../Filter/FilterDropdown";

const COLS = 26;
const ROW_HEIGHT = 25;
const COL_WIDTH = 120;
const HEADER_HEIGHT = 30;
const BUFFER_SIZE = 5; // Number of extra rows/cols to render for smooth scrolling

const columns = Array.from({ length: COLS }, (_, i) =>
  String.fromCharCode(65 + i)
);
const rows = Array.from({ length: 1000 }, (_, i) => i + 1);

interface CellData {
  value: string;
  format?: {
    bold?: boolean;
    italic?: boolean;
    underline?: boolean;
    fontSize?: string;
    fontFamily?: string;
    fontColor?: string;
    backgroundColor?: string;
    alignment?: string;
    borderStyle?: string;
    numberFormat?: string;
  };
}

interface VirtualizedGridProps {
  data: CellData[][];
  onCellChange: (row: number, col: number, value: string) => void;
  onFormatChange: (formatType: string, value: any) => void;
  filters: { [col: number]: any };
  selectedCell: { row: number; col: number };
  setSelectedCell: (cell: { row: number; col: number }) => void;
  selectedRange: { start: { row: number; col: number }; end: { row: number; col: number } } | null;
  setSelectedRange: (range: { start: { row: number; col: number }; end: { row: number; col: number } } | null) => void;
  setFilters: (filters: any) => void;
  onInsertRow?: () => void;
  onInsertColumn?: () => void;
  onDeleteRow?: () => void;
  onDeleteColumn?: () => void;
  clipboardData?: string;
  setClipboardData?: (v: string) => void;
}

const VirtualizedGrid = forwardRef<HTMLDivElement, VirtualizedGridProps>(function VirtualizedGrid({
  data,
  onCellChange,
  onFormatChange,
  filters,
  selectedCell,
  setSelectedCell,
  selectedRange,
  setSelectedRange,
  setFilters,
  onInsertRow,
  onInsertColumn,
  onDeleteRow,
  onDeleteColumn,
  clipboardData,
  setClipboardData,
}: VirtualizedGridProps, ref) {
  const gridRef = useRef<HTMLDivElement>(null);
  const [editingCell, setEditingCell] = useState<{ row: number; col: number }>({ row: -1, col: -1 });
  const [editValue, setEditValue] = useState("");
  const [isSelecting, setIsSelecting] = useState(false);
  const [selectionStart, setSelectionStart] = useState<{ row: number; col: number } | null>(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [containerHeight, setContainerHeight] = useState(600);
  const [containerWidth, setContainerWidth] = useState(800);
  const [isGridFocused, setIsGridFocused] = useState(false);
  const isCommittingEdit = useRef(false);

  // Calculate visible range based on scroll position
  const visibleRange = useMemo(() => {
    const startRow = Math.max(0, Math.floor(scrollTop / ROW_HEIGHT) - BUFFER_SIZE);
    const endRow = Math.min(data.length, Math.ceil((scrollTop + containerHeight) / ROW_HEIGHT) + BUFFER_SIZE);
    const startCol = Math.max(0, Math.floor(scrollLeft / COL_WIDTH) - BUFFER_SIZE);
    const endCol = Math.min(COLS, Math.ceil((scrollLeft + containerWidth) / COL_WIDTH) + BUFFER_SIZE);
    
    return { startRow, endRow, startCol, endCol };
  }, [scrollTop, scrollLeft, containerHeight, containerWidth, data.length]);

  // Grid-specific navigation functions
  const goToRowStart = useCallback(() => {
    setSelectedCell({ row: selectedCell.row, col: 0 });
    setSelectedRange(null);
    setSelectionStart(null);
  }, [selectedCell.row, setSelectedCell, setSelectedRange]);

  const goToRowEnd = useCallback(() => {
    setSelectedCell({ row: selectedCell.row, col: COLS - 1 });
    setSelectedRange(null);
    setSelectionStart(null);
  }, [selectedCell.row, setSelectedCell, setSelectedRange]);

  const goToSheetStart = useCallback(() => {
    setSelectedCell({ row: 0, col: 0 });
    setSelectedRange(null);
    setSelectionStart(null);
    if (gridRef.current) {
      gridRef.current.scrollTop = 0;
      gridRef.current.scrollLeft = 0;
    }
  }, [setSelectedCell, setSelectedRange]);

  const goToSheetEnd = useCallback(() => {
    setSelectedCell({ row: data.length - 1, col: COLS - 1 });
    setSelectedRange(null);
    setSelectionStart(null);
    if (gridRef.current) {
      gridRef.current.scrollTop = gridRef.current.scrollHeight;
      gridRef.current.scrollLeft = gridRef.current.scrollWidth;
    }
  }, [data.length, setSelectedCell, setSelectedRange]);

  const pageUp = useCallback(() => {
    const pageSize = Math.floor(containerHeight / ROW_HEIGHT);
    const newRow = Math.max(0, selectedCell.row - pageSize);
    setSelectedCell({ row: newRow, col: selectedCell.col });
    setSelectedRange(null);
    setSelectionStart(null);
  }, [selectedCell, containerHeight, setSelectedCell, setSelectedRange]);

  const pageDown = useCallback(() => {
    const pageSize = Math.floor(containerHeight / ROW_HEIGHT);
    const newRow = Math.min(data.length - 1, selectedCell.row + pageSize);
    setSelectedCell({ row: newRow, col: selectedCell.col });
    setSelectedRange(null);
    setSelectionStart(null);
  }, [selectedCell, data.length, containerHeight, setSelectedCell, setSelectedRange]);

  const editCell = useCallback(() => {
    if (selectedCell.row >= 0 && selectedCell.col >= 0) {
      setEditingCell({ row: selectedCell.row, col: selectedCell.col });
      setEditValue(data[selectedCell.row]?.[selectedCell.col]?.value || "");
    }
  }, [selectedCell, data]);

  const cancelSelection = useCallback(() => {
    setSelectedRange(null);
    setSelectionStart(null);
    setEditingCell({ row: -1, col: -1 });
    setEditValue("");
  }, [setSelectedRange]);

  const selectAll = useCallback(() => {
    setSelectedRange({
      start: { row: 0, col: 0 },
      end: { row: data.length - 1, col: COLS - 1 }
    });
  }, [data.length, setSelectedRange]);

  // Clipboard logic
  const handleCopySelection = useCallback(async () => {
    if (!selectedRange) {
      // Single cell
      const cell = data[selectedCell.row]?.[selectedCell.col]?.value || "";
      await navigator.clipboard.writeText(cell);
    } else {
      // Range
      const { start, end } = selectedRange;
      const minRow = Math.min(start.row, end.row);
      const maxRow = Math.max(start.row, end.row);
      const minCol = Math.min(start.col, end.col);
      const maxCol = Math.max(start.col, end.col);
      let text = "";
      for (let r = minRow; r <= maxRow; r++) {
        let rowVals = [];
        for (let c = minCol; c <= maxCol; c++) {
          rowVals.push(data[r]?.[c]?.value || "");
        }
        text += rowVals.join("\t");
        if (r < maxRow) text += "\n";
      }
      await navigator.clipboard.writeText(text);
    }
  }, [selectedCell, selectedRange, data]);

  const handleCutSelection = useCallback(async () => {
    await handleCopySelection();
    // Clear the selected cells
    if (!selectedRange) {
      onCellChange(selectedCell.row, selectedCell.col, "");
    } else {
      const { start, end } = selectedRange;
      const minRow = Math.min(start.row, end.row);
      const maxRow = Math.max(start.row, end.row);
      const minCol = Math.min(start.col, end.col);
      const maxCol = Math.max(start.col, end.col);
      for (let r = minRow; r <= maxRow; r++) {
        for (let c = minCol; c <= maxCol; c++) {
          onCellChange(r, c, "");
        }
      }
    }
  }, [selectedCell, selectedRange, onCellChange, handleCopySelection]);

  const handlePasteSelection = useCallback(async () => {
    const text = await navigator.clipboard.readText();
    const rows = text.split("\n");
    const startRow = selectedCell.row;
    const startCol = selectedCell.col;
    rows.forEach((row, rIdx) => {
      const cells = row.split("\t");
      cells.forEach((cell, cIdx) => {
        const targetRow = startRow + rIdx;
        const targetCol = startCol + cIdx;
        if (targetRow < data.length && targetCol < COLS) {
          onCellChange(targetRow, targetCol, cell);
        }
      });
    });
  }, [selectedCell, onCellChange, data.length]);

  // Update container dimensions
  useEffect(() => {
    const updateDimensions = () => {
      if (gridRef.current) {
        const rect = gridRef.current.getBoundingClientRect();
        setContainerHeight(rect.height);
        setContainerWidth(rect.width);
      }
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, []);

  // Handle scroll events
  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop: newScrollTop, scrollLeft: newScrollLeft } = e.currentTarget;
    setScrollTop(newScrollTop);
    setScrollLeft(newScrollLeft);
  }, []);

  // Safety mechanism to reset editing state if needed
  useEffect(() => {
    const resetEditingIfNeeded = () => {
      if (editingCell.row >= 0 && editingCell.col >= 0) {
        if (editingCell.row >= data.length || editingCell.col >= COLS) {
          setEditingCell({ row: -1, col: -1 });
          setEditValue("");
        }
      }
    };

    resetEditingIfNeeded();
  }, [editingCell, data.length]);

  // Add this helper function inside the VirtualizedGrid component, before handleKeyDown and handleEditKeyDown
  const scrollCellIntoView = useCallback((row: number, col: number) => {
    if (!gridRef.current) return;
    const grid = gridRef.current;
    const cellTop = row * ROW_HEIGHT;
    const cellBottom = cellTop + ROW_HEIGHT;
    const cellLeft = col * COL_WIDTH;
    const cellRight = cellLeft + COL_WIDTH;
    const visibleTop = grid.scrollTop;
    const visibleBottom = grid.scrollTop + containerHeight;
    const visibleLeft = grid.scrollLeft;
    const visibleRight = grid.scrollLeft + containerWidth;

    let newScrollTop = grid.scrollTop;
    let newScrollLeft = grid.scrollLeft;

    if (cellTop < visibleTop) newScrollTop = cellTop;
    else if (cellBottom > visibleBottom) newScrollTop = cellBottom - containerHeight;

    if (cellLeft < visibleLeft) newScrollLeft = cellLeft;
    else if (cellRight > visibleRight) newScrollLeft = cellRight - containerWidth;

    if (newScrollTop !== grid.scrollTop) grid.scrollTop = newScrollTop;
    if (newScrollLeft !== grid.scrollLeft) grid.scrollLeft = newScrollLeft;
  }, [containerHeight, containerWidth]);

  // Add a hidden textarea for paste fallback
  const hiddenTextareaRef = useRef<HTMLTextAreaElement>(null);

  // Only handle grid-level key events when not editing a cell
  const handleGridKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    // Only handle navigation/shortcuts if NOT editing
    if (!(editingCell.row === -1 && editingCell.col === -1)) return;
    setIsGridFocused(true);
    // Arrow keys: move selection
    if (["ArrowUp","ArrowDown","ArrowLeft","ArrowRight"].includes(e.key)) {
      e.preventDefault();
      let { row, col } = selectedCell;
      if (e.key === "ArrowUp") row = Math.max(0, row - 1);
      if (e.key === "ArrowDown") row = Math.min(data.length - 1, row + 1);
      if (e.key === "ArrowLeft") col = Math.max(0, col - 1);
      if (e.key === "ArrowRight") col = Math.min(COLS - 1, col + 1);
      setSelectedCell({ row, col });
      setSelectedRange(null);
      setSelectionStart(null);
      return;
    }
    // Enter or F2: enter edit mode
    if (e.key === "Enter" || e.key === "F2") {
      e.preventDefault();
      setEditingCell(selectedCell);
      setEditValue(data[selectedCell.row]?.[selectedCell.col]?.value || "");
      setTimeout(() => {
        const input = document.getElementById(`cell-input-${selectedCell.row}-${selectedCell.col}`);
        if (input) (input as HTMLInputElement).focus();
      }, 0);
      return;
    }
    // Tab/Shift+Tab: move selection right/left
    if (e.key === "Tab") {
      e.preventDefault();
      let { row, col } = selectedCell;
      if (e.shiftKey) col = Math.max(0, col - 1);
      else col = Math.min(COLS - 1, col + 1);
      setSelectedCell({ row, col });
      setSelectedRange(null);
      setSelectionStart(null);
      return;
    }
    // Handle copy
    if (e.ctrlKey && e.key.toLowerCase() === 'c') {
      e.preventDefault();
      handleCopySelection();
      if (!navigator.clipboard) {
        document.execCommand('copy');
      }
    }
    // Handle cut
    else if (e.ctrlKey && e.key.toLowerCase() === 'x') {
      e.preventDefault();
      handleCutSelection();
      if (!navigator.clipboard) {
        document.execCommand('cut');
      }
    }
    // Handle paste
    else if (e.ctrlKey && e.key.toLowerCase() === 'v') {
      e.preventDefault();
      if (navigator.clipboard) {
        handlePasteSelection();
      } else if (hiddenTextareaRef.current) {
        hiddenTextareaRef.current.value = '';
        hiddenTextareaRef.current.focus();
        setTimeout(() => {
          handlePasteSelection();
        }, 0);
      }
    }
    // Delete key
    else if (e.key === "Delete" || e.key === "Backspace") {
      e.preventDefault();
      if (selectedRange) {
        const { start, end } = selectedRange;
        const minRow = Math.min(start.row, end.row);
        const maxRow = Math.max(start.row, end.row);
        const minCol = Math.min(start.col, end.col);
        const maxCol = Math.max(start.col, end.col);
        for (let r = minRow; r <= maxRow; r++) {
          for (let c = minCol; c <= maxCol; c++) {
            onCellChange(r, c, "");
          }
        }
      } else {
        onCellChange(selectedCell.row, selectedCell.col, "");
      }
    }
  }, [editingCell, selectedCell, data, setEditingCell, setEditValue, setIsGridFocused, setSelectedCell, setSelectedRange, setSelectionStart, handleCopySelection, handleCutSelection, handlePasteSelection, onCellChange, selectionStart, selectedRange]);

  const isInSelectedRange = useCallback((row: number, col: number) => {
    if (!selectedRange) return false;
    const { start, end } = selectedRange;
    const minRow = Math.min(start.row, end.row);
    const maxRow = Math.max(start.row, end.row);
    const minCol = Math.min(start.col, end.col);
    const maxCol = Math.max(start.col, end.col);
    return row >= minRow && row <= maxRow && col >= minCol && col <= maxCol;
  }, [selectedRange]);

  const getCellStyle = useCallback((cellData: CellData, isSelected: boolean, isInRange: boolean, isHeader: boolean = false): React.CSSProperties => {
    const format = cellData?.format || {};
    const baseStyle: React.CSSProperties = {
      border: isSelected ? "2px solid #1976d2" : (isInRange ? "2px solid #90cdf4" : "1px solid #e2e8f0"),
      padding: "2px 4px",
      fontSize: format.fontSize || "12px",
      fontFamily: format.fontFamily || "Arial",
      color: format.fontColor || "#000000",
      backgroundColor: isSelected ? "#e3f2fd" : (isInRange ? "#e0f2fe" : (format.backgroundColor || "#ffffff")),
      fontWeight: format.bold ? "bold" : "normal",
      fontStyle: format.italic ? "italic" : "normal",
      textDecoration: format.underline ? "underline" : "none",
      textAlign: (format.alignment as "left" | "center" | "right") || "left",
      outline: "none",
      width: COL_WIDTH,
      height: isHeader ? HEADER_HEIGHT : ROW_HEIGHT,
      boxSizing: "border-box",
      position: "relative",
      display: "flex",
      alignItems: "center",
      overflow: "hidden",
    };

    if (format.borderStyle && format.borderStyle !== "none") {
      switch (format.borderStyle) {
        case "all":
          baseStyle.border = "2px solid #000000";
          break;
        case "outside":
          baseStyle.border = "2px solid #000000";
          break;
        case "bottom":
          baseStyle.borderBottom = "2px solid #000000";
          break;
        case "top":
          baseStyle.borderTop = "2px solid #000000";
          break;
        case "left":
          baseStyle.borderLeft = "2px solid #000000";
          break;
        case "right":
          baseStyle.borderRight = "2px solid #000000";
          break;
      }
    }

    if (isHeader) {
      baseStyle.backgroundColor = "#f8f9fa";
      baseStyle.fontWeight = "bold";
      baseStyle.borderBottom = "2px solid #dee2e6";
    }

    return baseStyle;
  }, []);

  const handleCellClick = useCallback((row: number, col: number) => {
    setSelectedCell({ row, col });
    setSelectedRange(null);
    setSelectionStart(null);
    setIsGridFocused(true);
  }, [setSelectedCell, setSelectedRange]);

  const handleCellDoubleClick = useCallback((row: number, col: number) => {
    setEditingCell({ row, col });
    setEditValue(data[row]?.[col]?.value || "");
  }, [data]);

  const handleCellMouseDown = useCallback((row: number, col: number) => {
    setIsSelecting(true);
    setSelectionStart({ row, col });
    setSelectedCell({ row, col });
    setSelectedRange(null);
    setIsGridFocused(true);
  }, [setSelectedCell, setSelectedRange]);

  const handleCellMouseEnter = useCallback((row: number, col: number) => {
    if (isSelecting && selectionStart) {
      setSelectedRange({
        start: selectionStart,
        end: { row, col }
      });
    }
  }, [isSelecting, selectionStart, setSelectedRange]);

  const handleMouseUp = () => {
    setIsSelecting(false);
  };

  const handleEditComplete = useCallback((value: string) => {
    if (editingCell.row >= 0 && editingCell.col >= 0) {
      onCellChange(editingCell.row, editingCell.col, value);
    }
    setEditingCell({ row: -1, col: -1 });
    setEditValue("");
  }, [editingCell, onCellChange]);

  const handleEditCancel = useCallback(() => {
    setEditingCell({ row: -1, col: -1 });
    setEditValue("");
  }, []);

  const handleEditKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === "Tab") {
      e.preventDefault();
      isCommittingEdit.current = true;
      handleEditComplete(editValue);
      let nextRow = editingCell.row;
      let nextCol = editingCell.col;
      if (e.key === "Enter" && !e.shiftKey) nextRow = Math.min(data.length - 1, editingCell.row + 1);
      if (e.key === "Enter" && e.shiftKey) nextRow = Math.max(0, editingCell.row - 1);
      if (e.key === "Tab" && !e.shiftKey) nextCol = Math.min(COLS - 1, editingCell.col + 1);
      if (e.key === "Tab" && e.shiftKey) nextCol = Math.max(0, editingCell.col - 1);
      setSelectedCell({ row: nextRow, col: nextCol });
      setTimeout(() => { isCommittingEdit.current = false; }, 0);
    } else if (e.ctrlKey && ["ArrowUp","ArrowDown","ArrowLeft","ArrowRight"].includes(e.key)) {
      e.preventDefault();
      isCommittingEdit.current = true;
      handleEditComplete(editValue);
      let nextRow = editingCell.row;
      let nextCol = editingCell.col;
      if (e.key === "ArrowUp") nextRow = Math.max(0, editingCell.row - 1);
      if (e.key === "ArrowDown") nextRow = Math.min(data.length - 1, editingCell.row + 1);
      if (e.key === "ArrowLeft") nextCol = Math.max(0, editingCell.col - 1);
      if (e.key === "ArrowRight") nextCol = Math.min(COLS - 1, editingCell.col + 1);
      setSelectedCell({ row: nextRow, col: nextCol });
      setTimeout(() => { isCommittingEdit.current = false; }, 0);
    } else if (e.key === "Escape") {
      e.preventDefault();
      isCommittingEdit.current = true;
      handleEditCancel();
      setTimeout(() => { isCommittingEdit.current = false; }, 0);
    }
    // Do NOT intercept plain Arrow keys—let browser move caret
  }, [editValue, handleEditComplete, handleEditCancel, data.length, editingCell]);

  // Ensure grid is always focused when clicked, focused, or receives a keydown
  const handleGridClick = useCallback(() => {
    setIsGridFocused(true);
    if (gridRef.current) gridRef.current.focus();
  }, []);

  const handleGridFocus = useCallback(() => {
    setIsGridFocused(true);
  }, []);

  const handleGridBlur = useCallback(() => {
    setIsGridFocused(false);
  }, []);

  // Scroll to selected cell when it changes
  useEffect(() => {
    scrollCellIntoView(selectedCell.row, selectedCell.col);
  }, [selectedCell, scrollCellIntoView]);

  // Calculate total grid width and height
  const totalWidth = 50 + COLS * COL_WIDTH;
  const totalHeight = HEADER_HEIGHT + data.length * ROW_HEIGHT;

  // Paste from App clipboardData
  useEffect(() => {
    if (clipboardData && clipboardData.length > 0) {
      const rows = clipboardData.split("\n");
      const startRow = selectedCell.row;
      const startCol = selectedCell.col;
      rows.forEach((row, rIdx) => {
        const cells = row.split("\t");
        cells.forEach((cell, cIdx) => {
          const targetRow = startRow + rIdx;
          const targetCol = startCol + cIdx;
          if (targetRow < data.length && targetCol < COLS) {
            onCellChange(targetRow, targetCol, cell);
          }
        });
      });
      if (setClipboardData) setClipboardData("");
    }
  }, [clipboardData]);

  // Add formatting and undo/redo shortcuts
  useEffect(() => {
    const handleFormatShortcut = (e: KeyboardEvent) => {
      if (editingCell.row >= 0 && editingCell.col >= 0) return;
      if (e.ctrlKey && !e.shiftKey && !e.altKey) {
        if (e.key.toLowerCase() === 'b') {
          e.preventDefault();
          onFormatChange('bold', !(data[selectedCell.row][selectedCell.col]?.format?.bold));
        } else if (e.key.toLowerCase() === 'i') {
          e.preventDefault();
          onFormatChange('italic', !(data[selectedCell.row][selectedCell.col]?.format?.italic));
        } else if (e.key.toLowerCase() === 'u') {
          e.preventDefault();
          onFormatChange('underline', !(data[selectedCell.row][selectedCell.col]?.format?.underline));
        } else if (e.key.toLowerCase() === 'z') {
          e.preventDefault();
          if (window.history && window.history.length > 0) window.history.back(); // Placeholder, should call undo handler
        } else if (e.key.toLowerCase() === 'y') {
          e.preventDefault();
          if (window.history && window.history.length > 0) window.history.forward(); // Placeholder, should call redo handler
        }
      }
    };
    document.addEventListener('keydown', handleFormatShortcut);
    return () => document.removeEventListener('keydown', handleFormatShortcut);
  }, [editingCell, selectedCell, data, onFormatChange]);

  return (
    <div
      ref={(node) => {
        gridRef.current = node;
        if (typeof ref === 'function') ref(node);
        else if (ref) (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
      }}
      className="virtualized-grid"
      style={{
        height: "100%",
        width: "100%",
        overflow: "auto",
        position: "relative",
        backgroundColor: "#ffffff",
        outline: isGridFocused ? "2px solid #1976d2" : "none",
        cursor: "default"
      }}
      onScroll={handleScroll}
      onClick={handleGridClick}
      onFocus={handleGridFocus}
      onBlur={handleGridBlur}
      onKeyDown={handleGridKeyDown}
      tabIndex={0}
    >
      {/* Hidden textarea for paste fallback */}
      <textarea ref={hiddenTextareaRef} style={{ position: 'absolute', left: '-9999px', top: '-9999px', opacity: 0 }} tabIndex={-1} aria-hidden="true" />
      {/* The scrollable content area (headers + cells) */}
      <div
        style={{
          position: "relative",
          width: totalWidth,
          height: totalHeight,
        }}
      >
        {/* Column Headers */}
        {columns.slice(visibleRange.startCol, visibleRange.endCol).map((col, index) => {
          const colIndex = visibleRange.startCol + index;
          return (
            <div
              key={col}
              style={{
                width: COL_WIDTH,
                height: HEADER_HEIGHT,
                border: "1px solid #e2e8f0",
                backgroundColor: "#f8f9fa",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: "bold",
                fontSize: "12px",
                borderBottom: "2px solid #dee2e6",
                position: "absolute",
                left: 50 + colIndex * COL_WIDTH,
                top: 0,
                zIndex: 10,
              }}
            >
              {col}
              <FilterDropdown
                column={colIndex}
                filters={filters}
                setFilters={setFilters}
              />
            </div>
          );
        })}
        {/* Row Headers and Data Cells */}
        {Array.from({ length: visibleRange.endRow - visibleRange.startRow }, (_, index) => {
          const rowIndex = visibleRange.startRow + index;
          const rowData = data[rowIndex] || [];
          // Row header
          return (
            <React.Fragment key={rowIndex}>
              <div
                style={{
                  width: 50,
                  height: ROW_HEIGHT,
                  border: "1px solid #e2e8f0",
                  backgroundColor: "#f8f9fa",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: "bold",
                  fontSize: "12px",
                  position: "sticky",
                  left: 0,
                  top: HEADER_HEIGHT + rowIndex * ROW_HEIGHT,
                  zIndex: 15,
                  boxSizing: "border-box",
                }}
              >
                {rowIndex + 1}
              </div>
              {/* Data cells */}
              {Array.from({ length: visibleRange.endCol - visibleRange.startCol }, (_, colIndex) => {
                const actualColIndex = visibleRange.startCol + colIndex;
                const cellData = rowData[actualColIndex] || { value: "" };
                const isSelected = selectedCell.row === rowIndex && selectedCell.col === actualColIndex;
                const isInRange = isInSelectedRange(rowIndex, actualColIndex);
                const isEditing = editingCell.row === rowIndex && editingCell.col === actualColIndex;
                return (
                  <div
                    key={`${rowIndex}-${actualColIndex}`}
                    style={{
                      ...getCellStyle(cellData, isSelected, isInRange),
                      position: "absolute",
                      left: 50 + actualColIndex * COL_WIDTH,
                      top: HEADER_HEIGHT + rowIndex * ROW_HEIGHT,
                      width: COL_WIDTH,
                      height: ROW_HEIGHT,
                    }}
                    onClick={() => handleCellClick(rowIndex, actualColIndex)}
                    onDoubleClick={() => handleCellDoubleClick(rowIndex, actualColIndex)}
                    onMouseDown={() => handleCellMouseDown(rowIndex, actualColIndex)}
                    onMouseEnter={() => handleCellMouseEnter(rowIndex, actualColIndex)}
                  >
                    {isEditing ? (
                      <input
                        id={`cell-input-${rowIndex}-${actualColIndex}`}
                        type="text"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onKeyDown={handleEditKeyDown}
                        onBlur={() => {
                          handleEditComplete(editValue);
                          isCommittingEdit.current = false;
                        }}
                        style={{
                          width: "100%",
                          height: "100%",
                          border: "none",
                          outline: "none",
                          background: "transparent",
                          fontSize: "inherit",
                          fontFamily: "inherit",
                          color: "inherit",
                        }}
                        autoFocus
                      />
                    ) : (
                      <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {cellData.value}
                      </span>
                    )}
                  </div>
                );
              })}
            </React.Fragment>
          );
        })}
        {/* Row headers for visible rows (sticky) */}
        {Array.from({ length: visibleRange.endRow - visibleRange.startRow }, (_, index) => {
          const rowIndex = visibleRange.startRow + index;
          return (
            <div
              key={`row-header-${rowIndex}`}
              style={{
                width: 50,
                height: ROW_HEIGHT,
                border: "1px solid #e2e8f0",
                backgroundColor: "#f8f9fa",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: "bold",
                fontSize: "12px",
                position: "absolute",
                left: 0,
                top: HEADER_HEIGHT + rowIndex * ROW_HEIGHT,
                zIndex: 15,
                boxSizing: "border-box",
              }}
            >
              {rowIndex + 1}
            </div>
          );
        })}
      </div>
      {/* Mouse up handler for selection */}
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 1,
          pointerEvents: "none",
        }}
        onMouseUp={handleMouseUp}
      />
    </div>
  );
});

export default VirtualizedGrid; 